import React from 'react';
import { AlertTriangle, DollarSign, X } from 'lucide-react';
import styles from './SelfPricingPriceConfirmModal.module.css';

type Props = {
  ticketCount: number;
  summary?: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/** Confirm before changing list price for self-pricing clients. */
export function SelfPricingPriceConfirmModal({
  ticketCount,
  summary,
  loading = false,
  onCancel,
  onConfirm,
}: Props) {
  const ticketLabel = ticketCount > 1 ? `${ticketCount} tickets` : 'this ticket';

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconWrap}>
              <DollarSign color="#b45309" size={18} />
            </div>
            <div className={styles.titleBlock}>
              <div className={styles.title}>Confirm Price Change</div>
              <div className={styles.subtitle}>
                {loading
                  ? 'Please wait — updating price on Victory Live'
                  : 'Self-pricing client — confirm before applying'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            disabled={loading}
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className={styles.processing} role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <div className={styles.processingText}>
              <b>Updating price…</b>
              <p className={styles.processingHint}>
                Do not close this window until it finishes.
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.warning}>
            <AlertTriangle color="#b45309" size={18} />
            <div>
              <b>Do you want to change the price?</b>
              <p>
                {summary ||
                  `This will update the list price for ${ticketLabel} belonging to a self-pricing client.`}
              </p>
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} disabled={loading} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={loading}
            onClick={() => void onConfirm()}
          >
            {loading ? 'Updating…' : 'Yes, Change Price'}
          </button>
        </div>
      </div>
    </div>
  );
}
